import { createApiClient } from '@elebyte/api-client';
import { setCookie, deleteCookie, COOKIE_NAMES } from '../utils/cookies';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.elebyte.io';
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.elebyte.io';

// Create API client with shared token refresh logic from @elebyte/api-client
const api = createApiClient({
  baseURL: API_URL,
  authStrategy: 'bearer',
  timeout: 30000,
  getToken: () => localStorage.getItem('accessToken'),
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token');

    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) throw new Error('Refresh failed');
    const data = await response.json();
    localStorage.setItem('accessToken', data.idToken);
    setCookie(COOKIE_NAMES.ACCESS_TOKEN, data.idToken, 1);
    return data.idToken;
  },
  onUnauthorized: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    deleteCookie(COOKIE_NAMES.ACCESS_TOKEN);
    deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
    window.location.href = `${AUTH_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
  },
});

export default api;

// =============================================================================
// Support API Functions
// =============================================================================

// Tickets
export const ticketsApi = {
  list: (params = {}) =>
    api.get('/api/support/tickets', { params }),

  get: (id) =>
    api.get(`/api/support/tickets/${id}`),

  create: (data) =>
    api.post('/api/support/tickets', data),

  update: (id, data) =>
    api.put(`/api/support/tickets/${id}`, data),

  addMessage: (id, message) =>
    api.post(`/api/support/tickets/${id}/messages`, { message }),

  uploadAttachment: (id, data) =>
    api.post(`/api/support/tickets/${id}/attachments`, data),

  downloadAttachment: (ticketId, attachmentId) =>
    api.get(`/api/support/tickets/${ticketId}/attachments/${attachmentId}`),

  submitFeedback: (id, rating, feedback) =>
    api.post(`/api/support/tickets/${id}/feedback`, { rating, feedback }),
};

// Knowledge Base
export const kbApi = {
  getCategories: () =>
    api.get('/api/support/kb/categories'),

  getCategory: (slug, params = {}) =>
    api.get(`/api/support/kb/categories/${slug}`, { params }),

  getArticles: (params = {}) =>
    api.get('/api/support/kb/articles', { params }),

  getArticle: (slug) =>
    api.get(`/api/support/kb/articles/${slug}`),

  getFeaturedArticles: (limit = 6) =>
    api.get('/api/support/kb/articles/featured', { params: { limit } }),

  search: (query, params = {}) =>
    api.get('/api/support/kb/search', { params: { q: query, ...params } }),

  submitFeedback: (slug, isHelpful, feedbackText = null) =>
    api.post(`/api/support/kb/articles/${slug}/feedback`, {
      is_helpful: isHelpful,
      feedback_text: feedbackText,
    }),

  trackView: (slug) =>
    api.post(`/api/support/kb/articles/${slug}/view`),
};

// Error Logging
export const loggingApi = {
  /**
   * Logs client-side errors to CloudWatch via backend
   * @param {Object} errorData - Error details to log
   * @param {string} errorData.message - Error message
   * @param {string} errorData.context - Context where error occurred
   * @param {Object} [errorData.metadata] - Additional error metadata
   * @param {Error} [errorData.error] - Original error object
   */
  logClientError: async (errorData) => {
    try {
      // Extract useful error details
      const payload = {
        message: errorData.message || 'Unknown error',
        context: errorData.context || 'unknown',
        metadata: {
          ...errorData.metadata,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          // Include error stack if available
          ...(errorData.error && {
            errorName: errorData.error.name,
            errorMessage: errorData.error.message,
            errorStack: errorData.error.stack?.split('\n').slice(0, 10).join('\n'), // Limit stack trace size
          }),
        },
        severity: errorData.severity || 'error',
      };

      // Send to backend for CloudWatch logging
      // Using support endpoint as it's the appropriate domain
      await api.post('/api/support/client-errors', payload);
    } catch (logError) {
      // Fail silently - don't let logging errors break the app
      // But keep one console.error for local dev debugging only
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to log client error:', logError);
      }
    }
  },
};

// Auth (uses Platform endpoints)
export const authApi = {
  getMe: () =>
    api.get('/api/auth/me'),

  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    deleteCookie(COOKIE_NAMES.ACCESS_TOKEN);
    deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
  },
};
